/**
 * The durable operation queue.
 *
 * The rule the brief states and this file exists to honour: **local data must
 * not be deleted simply because an HTTP request was sent.** An operation leaves
 * the queue only when the server has acknowledged it, or when a person has
 * looked at a conflict and decided what to do. Everything else — a timeout, a
 * 500, a flat battery mid-request — leaves the row exactly where it was, with an
 * attempt count and a time to try again.
 *
 * `opId` and `idempotencyKey` are minted once, when the operation is enqueued,
 * and never regenerated. That is what makes a retry a retry rather than a second
 * sale.
 */

import { db, getSyncMeta, updateSyncMeta } from "@/db/database";
import type { OperationType, OutboxOperation, SyncState } from "@/db/schema";
import { backoffDelayMs, isExhausted, MAX_ATTEMPTS } from "@/sync/states";

/**
 * How many operations may go up in one request.
 *
 * Mirrors `MAX_OPERATIONS` in `apps/sync/serializers.py`. Larger would be
 * refused; much smaller would leave a long-offline terminal draining for
 * half an hour.
 */
export const MAX_BATCH = 100;

/**
 * How long a claim lasts before the operation is treated as abandoned.
 *
 * A claim is only released by the push that made it — acknowledged, retried or
 * conflicted. If the app is closed or the till loses power while that push is
 * in flight, nothing ever releases it, and the sale sat in SYNCING for good:
 * counted as waiting, never sent again. So a claim is a lease. It stamps
 * `nextAttemptAt` with its expiry, and the next claim takes back anything still
 * SYNCING past it. Longer than the push's own 60-second timeout, so a slow but
 * live request is never reclaimed under its own feet — and if one ever were, the
 * idempotency key makes the second upload a DUPLICATE, which is a success.
 */
export const CLAIM_LEASE_MS = 3 * 60_000;

/**
 * Re-exported as this repository's public contract.
 *
 * Screens import these from here rather than from `@/db/schema`, so the local
 * database stays an implementation detail of the repository layer — the same
 * boundary `authRepository` keeps, and one an ESLint rule enforces. It caught
 * the sync centre reaching straight past it.
 */
export type { OperationType, OutboxOperation, SyncState };

export interface EnqueueInput {
  type: OperationType;
  entityId: string;
  idempotencyKey: string;
  payload: Record<string, unknown>;
  businessDate: string;
}

export const outboxRepository = {
  /**
   * Add an operation to the queue.
   *
   * `sequence` is monotonic per device and is what preserves causal order — a
   * shift must open before the sales inside it upload, and a sale must land
   * before its refund. It comes from `syncMeta`, bumped in the same transaction
   * so two enqueues cannot collide on a number.
   */
  async enqueue(input: EnqueueInput): Promise<OutboxOperation> {
    return db.transaction("rw", db.outbox, db.syncMeta, async () => {
      // `getSyncMeta`, not a raw read: the row does not exist on a fresh
      // install, and `db.syncMeta.update` on a missing key is a silent no-op —
      // which would leave every operation with sequence 1 and destroy the causal
      // ordering the whole queue depends on.
      const meta = await getSyncMeta();
      const sequence = meta.operationSequence + 1;

      const operation: OutboxOperation = {
        opId: crypto.randomUUID(),
        type: input.type,
        entityId: input.entityId,
        idempotencyKey: input.idempotencyKey,
        payload: input.payload,
        businessDate: input.businessDate,
        state: "PENDING",
        attempts: 0,
        // Due immediately. A sale made while online should upload before the
        // cashier has finished handing over the change.
        nextAttemptAt: Date.now(),
        lastError: null,
        createdAt: Date.now(),
        sequence,
        batchId: null,
      };

      await db.outbox.add(operation);
      await updateSyncMeta({ operationSequence: sequence });
      return operation;
    });
  },

  /**
   * The next operations due for an attempt, in causal order.
   *
   * Claimed by moving them to SYNCING inside a transaction, so two engine ticks
   * — one from a timer, one from the "connection restored" event — cannot both
   * pick up the same sale. Takes back expired claims first; see
   * `CLAIM_LEASE_MS`.
   */
  /**
   * `limit` matches the server's per-batch cap.
   *
   * It was 20 while every operation was its own request, where the number barely
   * mattered. Now that the whole claim goes up in one batch it is the drain
   * *rate*: a terminal back from a week offline with five hundred queued
   * operations cleared twenty a minute — twenty-five minutes of a shopkeeper
   * watching a counter tick down for no reason.
   */
  async claimDue(limit = MAX_BATCH): Promise<OutboxOperation[]> {
    return db.transaction("rw", db.outbox, async () => {
      const now = Date.now();
      const abandoned = await db.outbox
        .where("[state+nextAttemptAt]")
        .between(["SYNCING", 0], ["SYNCING", now], true, true)
        .toArray();
      await Promise.all(
        abandoned.map((operation) =>
          db.outbox.update(operation.opId, {
            state: "PENDING",
            nextAttemptAt: now,
          }),
        ),
      );

      const due = await db.outbox
        .where("[state+nextAttemptAt]")
        .between(["PENDING", 0], ["PENDING", now], true, true)
        .sortBy("sequence");

      const claimed = due.slice(0, limit);
      const leaseExpiry = now + CLAIM_LEASE_MS;
      await Promise.all(
        claimed.map((operation) =>
          db.outbox.update(operation.opId, {
            state: "SYNCING",
            nextAttemptAt: leaseExpiry,
          }),
        ),
      );
      return claimed.map((operation) => ({
        ...operation,
        state: "SYNCING",
        nextAttemptAt: leaseExpiry,
      }));
    });
  },

  /**
   * Hand claimed work back untouched: no attempt counted, due again at once.
   *
   * For work a till cannot send on its own credential — queued before
   * operations named their cashier — while nobody is signed in to send it.
   */
  async release(opIds: string[]): Promise<void> {
    await Promise.all(
      opIds.map((opId) => db.outbox.update(opId, { state: "PENDING", nextAttemptAt: Date.now() })),
    );
  },

  /** The server has it. Only now may the operation leave the queue. */
  async acknowledge(opId: string): Promise<void> {
    await db.outbox.update(opId, { state: "SYNCED", lastError: null });
  },

  /**
   * The attempt failed and may be worth repeating.
   *
   * Back to PENDING with a backoff, until the attempt ceiling — after which it
   * becomes FAILED and stops consuming battery and data on a request that is
   * evidently never going to work. FAILED is *not* deleted: it is exactly what
   * the sync centre exists to show somebody.
   *
   * **Unless the failure says nothing about the operation.** No signal, an
   * expired login, a server that is down: a till offline for three weeks would
   * otherwise dead-letter its whole queue, and stop trying at exactly the
   * moment the signal came back. Those `transient` failures still count for the
   * backoff, whose ladder is capped, but never make an operation FAILED.
   */
  async retryLater(
    opId: string,
    error: string,
    { transient = false }: { transient?: boolean } = {},
  ): Promise<void> {
    const operation = await db.outbox.get(opId);
    if (!operation) return;

    const attempts = operation.attempts + 1;
    await db.outbox.update(opId, {
      attempts,
      state: !transient && isExhausted(attempts) ? "FAILED" : "PENDING",
      nextAttemptAt: Date.now() + backoffDelayMs(attempts),
      lastError: error.slice(0, 500),
    });
  },

  /**
   * The server disagreed in a way retrying cannot fix.
   *
   * A 4xx that is not a timeout or a rate limit means the request is wrong, not
   * unlucky — repeating it forever would be a battery drain and a lie. The
   * operation is parked as CONFLICT and a person decides.
   */
  async conflict(opId: string, detail: string, serverRecord: unknown = null): Promise<void> {
    const operation = await db.outbox.get(opId);
    if (!operation) return;

    await db.transaction("rw", db.outbox, db.conflicts, async () => {
      await db.outbox.update(opId, { state: "CONFLICT", lastError: detail.slice(0, 500) });
      await db.conflicts.add({
        id: crypto.randomUUID(),
        opId,
        entityType: operation.type,
        entityId: operation.entityId,
        detail: detail.slice(0, 500),
        serverRecord: (serverRecord as Record<string, unknown> | null) ?? null,
        localRecord: operation.payload,
        raisedAt: new Date().toISOString(),
        resolvedAt: null,
      });
    });
  },

  /** Put a failed or conflicted operation back in the queue, by hand. */
  async requeue(opId: string): Promise<void> {
    await db.outbox.update(opId, {
      state: "PENDING",
      attempts: 0,
      nextAttemptAt: Date.now(),
      lastError: null,
    });
  },

  /**
   * Discard an operation a person has decided to abandon.
   *
   * The only path that removes an unacknowledged row, and it requires somebody
   * to have looked at it in the sync centre. Nothing automatic deletes work.
   */
  async abandon(opId: string): Promise<void> {
    await db.transaction("rw", db.outbox, db.conflicts, async () => {
      await db.outbox.delete(opId);
      const raised = await db.conflicts.where("opId").equals(opId).toArray();
      await Promise.all(
        raised.map((row) => db.conflicts.update(row.id, { resolvedAt: new Date().toISOString() })),
      );
    });
  },

  counts: async () => {
    const all = await db.outbox.toArray();
    return {
      pending: all.filter((op) => op.state === "PENDING" || op.state === "SYNCING").length,
      failed: all.filter((op) => op.state === "FAILED").length,
      conflicts: all.filter((op) => op.state === "CONFLICT").length,
      oldest: all
        .filter((op) => op.state !== "SYNCED")
        .reduce<number | null>(
          (oldest, op) => (oldest === null ? op.createdAt : Math.min(oldest, op.createdAt)),
          null,
        ),
    };
  },

  list: (states: SyncState[]) =>
    db.outbox.filter((operation) => states.includes(operation.state)).sortBy("sequence"),

  /**
   * Remove acknowledged operations older than the retention window.
   *
   * SYNCED rows only — the server has them, so the local copy is a convenience.
   * Anything unacknowledged is untouched however old it is, because "we could
   * not upload this for a fortnight" is not a reason to throw away a day's
   * takings.
   */
  async prune(olderThanMs = 7 * 24 * 60 * 60 * 1000): Promise<number> {
    const cutoff = Date.now() - olderThanMs;
    return db.outbox
      .filter((operation) => operation.state === "SYNCED" && operation.createdAt < cutoff)
      .delete();
  },

  MAX_ATTEMPTS,
};
